import { useEffect, useState } from 'react';
import { apiFetch } from '@lib/apiClient';

/**
 * One device photo in a Review / Archive card. The image bytes are served by GET /api/intake/review/photo,
 * which is bearer-gated - and an <img src> cannot send the X-Cdx-Bearer custom header - so we fetch the blob
 * through apiFetch (which attaches the token) and render an object URL, revoked on unmount.
 *
 * size: 'thumb' (default, 96x96 cropped - Archive's compact list) or 'large' (full-width, object-contain so
 * the WHOLE strip is readable inline without a click - the Review tab, so the reviewer compares the image to
 * the recorded result at a glance). Either way the image still links to the full-resolution original.
 */
export function ReviewPhoto({
  campaign,
  recordId,
  index,
  size = 'thumb',
}: {
  campaign: string;
  recordId: string;
  index: number;
  size?: 'thumb' | 'large';
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);
  const large = size === 'large';

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    (async () => {
      try {
        const res = await apiFetch(
          `/api/intake/review/photo?campaign=${encodeURIComponent(campaign)}&recordId=${encodeURIComponent(
            recordId,
          )}&i=${index}`,
        );
        if (!res.ok) {
          if (!cancelled) setFailed(true);
          return;
        }
        const blob = await res.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch {
        if (!cancelled) setFailed(true);
      }
    })();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [campaign, recordId, index]);

  if (failed) {
    return (
      <div
        className={`flex items-center justify-center rounded-cdx border border-cdx-border-subtle bg-cdx-surface-1 text-center text-cdx-fg-muted ${
          large ? 'h-64 w-full text-sm' : 'h-24 w-24 text-[10px]'
        }`}
      >
        photo unavailable
      </div>
    );
  }
  if (!url) {
    return (
      <div
        className={`animate-pulse rounded-cdx border border-cdx-border-subtle bg-cdx-surface-2 ${large ? 'h-96 w-full' : 'h-24 w-24'}`}
      />
    );
  }
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block" title="Open full size">
      <img
        src={url}
        alt="Test device"
        className={
          large
            ? 'max-h-[52rem] w-full rounded-cdx border border-cdx-border-subtle bg-cdx-surface-1 object-contain'
            : 'h-24 w-24 rounded-cdx border border-cdx-border-subtle object-cover'
        }
      />
    </a>
  );
}
